import { useState } from 'react';
import styled, { css } from 'styled-components';
import { isMobile } from 'react-device-detect';
import { Row, Col } from 'react-bootstrap';

import { useSwiperRef } from '_hooks';
import { config } from '_config';
import { theme } from '_constants/theme';

import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Pagination, Navigation } from 'swiper';

import Text from '_components/atomic/layouts/Card/Text';

const StarSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    height={12}
    width={12}
    viewBox="0 0 24 24"
    fill="none"
  >
    <g>
      <path
        d="M15.5621 4.44723C14.4311 2.18521 13.8656 1.0542 13.126 0.681093C12.4179 0.32386 11.5822 0.32386 10.874 0.681091C10.1344 1.05419 9.56892 2.18521 8.43791 4.44724C8.13099 5.06106 7.97754 5.36797 7.76834 5.60795C7.49876 5.91719 7.15723 6.15534 6.77386 6.30139C6.47635 6.41473 6.14153 6.45193 5.47188 6.52634C3.13977 6.78546 1.97372 6.91502 1.39896 7.38952C0.657283 8.00182 0.332614 8.9853 0.563974 9.91882C0.743268 10.6423 1.61602 11.4527 3.36154 13.0735C3.90529 13.5784 4.17717 13.8309 4.35485 14.1183C4.62987 14.5631 4.75847 15.083 4.72253 15.6047C4.69931 15.9418 4.57828 16.2868 4.33623 16.9766C3.54952 19.2187 3.15617 20.3398 3.30443 21.0286C3.53549 22.1022 4.4401 22.8993 5.53423 22.9934C6.23626 23.0538 7.29889 22.5225 9.42416 21.4598L10.2112 21.0663C10.8671 20.7384 11.195 20.5744 11.539 20.5099C11.8437 20.4527 12.1564 20.4527 12.461 20.5099C12.805 20.5744 13.133 20.7384 13.7889 21.0663L14.5759 21.4598C16.7012 22.5225 17.7638 23.0538 18.4658 22.9934C19.5599 22.8993 20.4646 22.1022 20.6956 21.0286C20.8439 20.3398 20.4505 19.2187 19.6638 16.9766C19.4218 16.2868 19.3007 15.9418 19.2775 15.6047C19.2416 15.083 19.3702 14.5631 19.6452 14.1183C19.8229 13.8309 20.0948 13.5784 20.6385 13.0735C22.384 11.4527 23.2568 10.6423 23.4361 9.91882C23.6674 8.9853 23.3428 8.00182 22.6011 7.38952C22.0263 6.91502 20.8603 6.78546 18.5282 6.52634C17.8585 6.45193 17.5237 6.41473 17.2262 6.30139C16.8428 6.15534 16.5013 5.91719 16.2317 5.60795C16.0225 5.36797 15.8691 5.06106 15.5621 4.44723Z"
        fill="#ffbe00"
      />
    </g>
  </svg>
);

const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    height={16}
    width={16}
    viewBox="0 0 24 24"
    fill="none"
  >
    <g>
      <path
        d="M10 6L16 12L10 18"
        stroke="#1D2939"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

const ChevronLeft = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    height={16}
    width={16}
    viewBox="0 0 24 24"
    fill="none"
    style={{ transform: 'rotate(180deg)' }}
  >
    <g>
      <path
        d="M10 6L16 12L10 18"
        stroke="#1D2939"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

const StyledContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 84%;

  position: relative;

  .swiper {
    height: 612px;
  }

  @media ${theme.devices.tablet} {
    width: 100%;

    .swiper {
      height: unset;
    }
  }
`;

const StyledOuterCardContainer = styled.div`
  height: 100%;
  padding: ${theme.spaces.smallX}px;
  border-radius: 40px;
  background-color: ${theme.colors.gray[50]};
  border: 1px solid ${theme.colors.gray[200]};

  @media ${theme.devices.tablet} {
    height: 832px;
  }
`;

const StyledInnerCardContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
  border-radius: 32px;
  border: 1px solid ${theme.colors.gray[200]};
  box-shadow: 0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);

  @media ${theme.devices.tablet} {
    flex-direction: column;
  }
`;

const StyledCardImageContainer = styled.div`
  picture {
    height: 100%;

    img {
      height: 100%;
      width: 314px;
      border-top-left-radius: 32px;
      border-bottom-left-radius: 32px;
      object-fit: cover;
    }
  }

  @media ${theme.devices.tablet} {
    picture {
      img {
        width: 100%;
        border-top-left-radius: 32px;
        border-top-right-radius: 32px;
        border-bottom-left-radius: unset;
        object-fit: cover;
      }
    }
  }
`;

const StyledCardBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  flex-grow: 1;
  padding: ${theme.spaces.large6X}px;
  border-top-right-radius: 32px;
  border-bottom-right-radius: 32px;
  background-color: ${theme.colors.white};

  > *:nth-child(2) {
    flex-grow: 1;
  }

  @media ${theme.devices.tablet} {
    min-width: unset;
    width: 100%;
    padding: ${theme.spaces.large}px;
    border-top-right-radius: unset;
    border-bottom-left-radius: 32px;
  }
`;

const StyledCardLogo = styled.div`
  margin-bottom: ${theme.spaces.large2X}px;
`;

const StyledCardFooter = styled.div`
  width: 100%;
`;

const StyledCardRating = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spaces.small2X}px;
  margin-right: 30px;

  @media ${theme.devices.tablet} {
    justify-content: flex-start;
    padding-top: ${theme.spaces.regular}px;
    width: 100%;
  }
`;

const StyledCardNav = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  gap: ${theme.spaces.regular}px;
  flex-grow: 1;
  position: absolute;
  bottom: 60px;
  right: calc(10% + 40px);
  z-index: 1;

  @media ${theme.devices.tablet} {
    bottom: 32px;
    left: 32px;
    right: 32px;
    justify-content: space-between;
  }
`;

const StyledNavButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 56px;
  width: 56px;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.gray[50]};

    svg {
      path {
        stroke: ${theme.colors.orange[600]};
      }
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}

  @media ${theme.devices.tablet} {
    background-color: ${theme.colors.white};
  }
`;

const FeedbackSwiper = () => {
  const [activeIndex, setActiveIndex] = useState(2);
  const [prevEl, prevElRef] = useSwiperRef();
  const [nextEl, nextElRef] = useSwiperRef();

  return (
    <StyledContainer>
      <Swiper
        effect={'coverflow'}
        grabCursor={true}
        centeredSlides={true}
        slidesPerView={isMobile ? 1 : 1.2}
        initialSlide={3}
        loop={isMobile}
        speed={800}
        coverflowEffect={{
          slideShadows: false,
        }}
        navigation={{ prevEl, nextEl }}
        modules={[EffectCoverflow, Pagination, Navigation]}
        onSlideChange={({ activeIndex }) => setActiveIndex(activeIndex)}
      >
        <SwiperSlide>
          <StyledOuterCardContainer>
            <StyledInnerCardContainer>
              <StyledCardImageContainer>
                <picture>
                  <source
                    srcSet={`${config.basePath}/images/v4-chad-${
                      isMobile ? 'mobile' : 'desktop'
                    }.jpg`}
                    type="image/jpeg"
                  />
                  <img
                    src={`${config.basePath}/images/v4-chad-${
                      isMobile ? 'mobile' : 'desktop'
                    }.jpg`}
                    alt="Chad"
                  />
                </picture>
              </StyledCardImageContainer>
              <StyledCardBody>
                <StyledCardLogo>
                  <picture>
                    <source
                      srcSet={`${config.basePath}/images/v4-chad-logo.png`}
                      type="image/png"
                    />
                    <img
                      src={`${config.basePath}/images/v4-chad-logo.png`}
                      alt="Isa Paints"
                    />
                  </picture>
                </StyledCardLogo>
                <Text size={theme.fontSizes.large2X} weight={500} desktop>
                  Incorpified's team of experts took care of filing my paperwork
                  so I could get back behind the camera. For an LLC like mine,
                  this is the modern way to get your business started and keep
                  things on track as it grows.
                </Text>
                <Text
                  size={theme.fontSizes.medium}
                  weight={500}
                  bottomSpace={theme.spaces.regular}
                  mobile
                >
                  Incorpified's team of experts took care of filing my paperwork
                  so I could get back behind the camera. For an LLC like mine,
                  this is the modern way to get your business started and keep
                  things on track as it grows.
                </Text>
                <StyledCardFooter>
                  <Row>
                    <Col xs={12} md={6}>
                      <Text
                        size={theme.fontSizes.large4X}
                        weight={500}
                        bottomSpace={theme.spaces.regular}
                      >
                        Chad
                      </Text>
                      <Text
                        size={theme.fontSizes.regular}
                        weight={500}
                        bottomSpace={theme.spaces.small2X}
                      >
                        Owner of Chad Martel Photography
                      </Text>
                      <Text size={theme.fontSizes.small} weight={300}>
                        Jacksonville, FL
                      </Text>
                    </Col>
                    <Col xs={12} md={6} className="d-flex justify-content-end">
                      <StyledCardRating>
                        <StarSvg />
                        <StarSvg />
                        <StarSvg />
                        <StarSvg />
                        <StarSvg />
                      </StyledCardRating>
                    </Col>
                  </Row>
                </StyledCardFooter>
              </StyledCardBody>
            </StyledInnerCardContainer>
          </StyledOuterCardContainer>
        </SwiperSlide>
        <SwiperSlide>
          <StyledOuterCardContainer>
            <StyledInnerCardContainer>
              <StyledCardImageContainer>
                <picture>
                  <source
                    srcSet={`${config.basePath}/images/v4-stephanie-${
                      isMobile ? 'mobile' : 'desktop'
                    }.jpg`}
                    type="image/jpeg"
                  />
                  <img
                    src={`${config.basePath}/images/v4-stephanie-${
                      isMobile ? 'mobile' : 'desktop'
                    }.jpg`}
                    alt="Stephanie"
                  />
                </picture>
              </StyledCardImageContainer>
              <StyledCardBody>
                <StyledCardLogo>
                  <picture>
                    <source
                      srcSet={`${config.basePath}/images/v4-stephanie-logo.png`}
                      type="image/png"
                    />
                    <img
                      src={`${config.basePath}/images/v4-stephanie-logo.png`}
                      alt="Isa Paints"
                    />
                  </picture>
                </StyledCardLogo>
                <Text size={theme.fontSizes.large2X} weight={500} desktop>
                  Incorpified made starting my real estate brokerage a breeze.
                  They registered my LLC, helped open a bank account, and
                  connected me to a tax pro without ever leaving my house. It's
                  THE solution for people like me who need to get business done
                  before the kids get home from school.
                </Text>
                <Text
                  size={theme.fontSizes.medium}
                  weight={500}
                  bottomSpace={theme.spaces.regular}
                  mobile
                >
                  Incorpified made starting my real estate brokerage a breeze.
                  They registered my LLC, helped open a bank account, and
                  connected me to a tax pro without ever leaving my house. It's
                  THE solution for people like me who need to get business done
                  before the kids get home from school.
                </Text>
                <StyledCardFooter>
                  <Row>
                    <Col xs={12} md={6}>
                      <Text
                        size={theme.fontSizes.large4X}
                        weight={500}
                        bottomSpace={theme.spaces.regular}
                      >
                        Stephanie
                      </Text>
                      <Text
                        size={theme.fontSizes.regular}
                        weight={500}
                        bottomSpace={theme.spaces.small2X}
                      >
                        Owner of Next Door Realty
                      </Text>
                      <Text size={theme.fontSizes.small} weight={300}>
                        Austin, TX
                      </Text>
                    </Col>
                    <Col xs={12} md={6} className="d-flex justify-content-end">
                      <StyledCardRating>
                        <StarSvg />
                        <StarSvg />
                        <StarSvg />
                        <StarSvg />
                        <StarSvg />
                      </StyledCardRating>
                    </Col>
                  </Row>
                </StyledCardFooter>
              </StyledCardBody>
            </StyledInnerCardContainer>
          </StyledOuterCardContainer>
        </SwiperSlide>
        <SwiperSlide>
          <StyledOuterCardContainer>
            <StyledInnerCardContainer>
              <StyledCardImageContainer>
                <picture>
                  <source
                    srcSet={`${config.basePath}/images/v4-isabel-${
                      isMobile ? 'mobile' : 'desktop'
                    }.jpg`}
                    type="image/jpeg"
                  />
                  <img
                    src={`${config.basePath}/images/v4-isabel-${
                      isMobile ? 'mobile' : 'desktop'
                    }.jpg`}
                    alt="Isabel"
                  />
                </picture>
              </StyledCardImageContainer>
              <StyledCardBody>
                <StyledCardLogo>
                  <picture>
                    <source
                      srcSet={`${config.basePath}/images/v4-isa-logo.png`}
                      type="image/png"
                    />
                    <img
                      src={`${config.basePath}/images/v4-isa-logo.png`}
                      alt="Isa Paints"
                    />
                  </picture>
                </StyledCardLogo>
                <Text size={theme.fontSizes.large2X} weight={500} desktop>
                  My parents always said, “... and a glass of scotch in the
                  other.” With Incorpified, I get to be the CEO MY way. With
                  their expert help, I&apos;m a confident business owner and
                  feel in command when it comes to the parts of entrepreneurship
                  I&apos;ve never navigated before.
                </Text>
                <Text
                  size={theme.fontSizes.medium}
                  weight={500}
                  bottomSpace={theme.spaces.regular}
                  mobile
                >
                  My parents always said, “... and a glass of scotch in the
                  other.” With Incorpified, I get to be the CEO MY way. With
                  their expert help, I&apos;m a confident business owner and
                  feel in command when it comes to the parts of entrepreneurship
                  I&apos;ve never navigated before.
                </Text>
                <StyledCardFooter>
                  <Row>
                    <Col xs={12} md={6}>
                      <Text
                        size={theme.fontSizes.large4X}
                        weight={500}
                        bottomSpace={theme.spaces.regular}
                      >
                        Isabel
                      </Text>
                      <Text
                        size={theme.fontSizes.regular}
                        weight={500}
                        bottomSpace={theme.spaces.small2X}
                      >
                        Founder of Isa Paints
                      </Text>
                      <Text size={theme.fontSizes.small} weight={300}>
                        San Mateo, CA
                      </Text>
                    </Col>
                    <Col xs={12} md={6} className="d-flex justify-content-end">
                      <StyledCardRating>
                        <StarSvg />
                        <StarSvg />
                        <StarSvg />
                        <StarSvg />
                        <StarSvg />
                      </StyledCardRating>
                    </Col>
                  </Row>
                </StyledCardFooter>
              </StyledCardBody>
            </StyledInnerCardContainer>
          </StyledOuterCardContainer>
        </SwiperSlide>
      </Swiper>
      <StyledCardNav>
        <StyledNavButton
          ref={prevElRef}
          disabled={!isMobile && activeIndex === 0}
        >
          <ChevronLeft />
        </StyledNavButton>
        <StyledNavButton
          ref={nextElRef}
          disabled={!isMobile && activeIndex === 2}
        >
          <ChevronRight />
        </StyledNavButton>
      </StyledCardNav>
    </StyledContainer>
  );
};

export default FeedbackSwiper;
